import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  TextField,
  Button,
  Switch,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useSettings } from './SettingsContext';
import axios from 'axios';

const ColorBox = styled(Box)(({ bgcolor }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: bgcolor,
  borderRadius: '4px',
  marginBottom: '10px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: '0 auto', // 中央揃え
  maxWidth: '800px', // 最大幅を800pxに設定
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const Input = styled('input')({
  display: 'none',
});

// 各MP3ファイルのURL
const audioFiles = [
  `${process.env.PUBLIC_URL}/mail1.mp3`,
  `${process.env.PUBLIC_URL}/mail2.mp3`,
];

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const Settings = () => {
  const [settingsState, setSettingsState] = useState({
    avatar: '',
    displayName: '',
    theme: 'light',
    workTime: 25,
    shortBreakTime: 5,
    longBreakTime: 15,
    primaryColor: '#FE6B8B',
    secondaryColor: '#FF8E53',
    notificationsEnabled: false,
    volume: 50,
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const { updateSettings } = useSettings();
  const sound1Ref = useRef(new Audio(audioFiles[0]));
  const sound2Ref = useRef(new Audio(audioFiles[1]));
  const fileInputRef = useRef(null);

  // APIから設定を初期化
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/settings/fetch`, {
          withCredentials: true,
        });
        setSettingsState(data);
      } catch (error) {
        if ([401, 403].includes(error.response?.status)) {
          navigate('/login');
        } else {
          console.error('設定の取得に失敗しました:', error);
        }
      }
    };
    fetchSettings();
  }, [navigate]);

  // 共通の設定更新関数
  const updateSetting = async (key, value) => {
    try {
      setSettingsState((prev) => ({ ...prev, [key]: value }));
      await axios.post(`${API_BASE}/api/settings/upload/${key}`, { [key]: value }, {
        withCredentials: true,
      });
      updateSettings((prev) => ({ ...prev, [key]: value }));
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        navigate('/login');
      } else {
        console.error(`設定の更新に失敗しました (${key}):`, error);
      }
    }
  }

  // アバター変更
  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const avatarData = reader.result;
      updateSetting('avatar', avatarData);
      const formData = new FormData();
      formData.append("avatar", reader.result);
      try {
        const res = await axios.post(`${API_BASE}/api/settings/upload/avatar`, formData, {
          withCredentials: true, // クッキーを含めるために必要
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        updateSettings((prev) => ({ ...prev, avatar: res.data.avatar }));
        console.log("ファイルが正常にアップロードされました！");
      } catch (error) {
        console.error("ファイルのアップロードに失敗しました:", error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSliderChange = (key) => (_, value) => {
    setSettingsState((prev) => ({ ...prev, [key]: value }));
  };

  const handleColorChange = (key) => (color) => {
    setSettingsState((prev) => ({ ...prev, [key]: color.hex }));
    updateSetting(key, color.hex);
  };

  const vapidPublicKey = "BF7zmA3qDrJUOUU2OI5klQ--FxhR2U8Vuh9CPs2pHkRyUjRzLCLu0tqBkA8RWuITQmqruaKOzbJOKD_ZP-Edegc";

  const updateNotificationEnabled = async () => {
    if (!('serviceWorker' in navigator)) {
      alert('Service Worker not supported');
      return;
    }
  
    try {
      if (!settingsState.notificationsEnabled) {
        // 購読登録
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
  
        // サーバに購読情報送信
        await axios.post(`${API_BASE}/api/push/subscribe`, { subscription }, {
          withCredentials: true,
        });
      } else {
        // 購読解除
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
  
        if (subscription) {
          await subscription.unsubscribe();
          await axios.post(`${API_BASE}/api/push/unsubscribe`, {}, {
            withCredentials: true,
          });
        } else {
          alert('No subscription to unsubscribe');
          setSettingsState((prev) => ({ ...prev, notificationsEnabled: false }));
          return;
        }
      }
      setSettingsState((prev) => ({ ...prev, notificationsEnabled: !settingsState.notificationsEnabled }));
      updateSetting('notificationsEnabled', !settingsState.notificationsEnabled);
    } catch (error) {
      console.error('Failed to update notification setting:', error);
      alert('通知の設定変更に失敗しました。通信状況などを確認してください。');
    }
  };

  const deleteAccount = () => {
    axios.delete(`${API_BASE}/api/settings/delete`, {
      withCredentials: true
    }).then(res => {
      console.log('削除成功:', res.data);
      navigate('/login');
    }).catch(err => {
      console.error('削除エラー:', err);
    });
  }

  const isBase64 = (avatar) => {
    if (!avatar) return false;
    return avatar.match(/^data:(.+);base64,(.+)$/);
  }

  // 再生処理
  const playSound1 = () => {
    sound1Ref.current.play();
  };

  const playSound2 = () => {
    sound2Ref.current.play();
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {
              isBase64(settingsState.avatar) ?
              <Avatar
                src={settingsState.avatar ? settingsState.avatar : '/placeholder-user.png'}
                sx={{ width: 100, height: 100, cursor: 'pointer' }}
                onClick={() => fileInputRef.current?.click()}
              />
              :
              <Avatar
                src={settingsState.avatar ? settingsState.avatar : '/placeholder-user.png'}
                sx={{ width: 100, height: 100, cursor: 'pointer' }}
                onClick={() => fileInputRef.current?.click()}
              />
            }
            <label htmlFor="avatar-upload">
              <Input
                ref={fileInputRef}
                accept="image/*"
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <Button variant="contained" component="span">
                Upload
              </Button>
            </label>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Display Name"
          value={settingsState.displayName}
          onChange={(e) => {
            const value = e.target.value;
            setSettingsState((prev) => ({ ...prev, displayName: value }));
            updateSetting('displayName', value);
          }}
          margin="normal"
        />

        <FormControl fullWidth margin="normal">
          <Select
            value={settingsState.theme}
            onChange={(e) => updateSetting('theme', e.target.value)}
          >
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="dark">Dark</MenuItem>
          </Select>
        </FormControl>

        <StyledPaper elevation={2}>
          <Typography variant="h6">Color Settings</Typography>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography>Primary</Typography>
              <ColorBox bgcolor={settingsState.primaryColor} />
              <Box
                sx={{
                  '& .chrome-picker': {
                    width: '100% !important',
                    boxShadow: 'none',
                  },
                }}
              >
                <ChromePicker
                  color={settingsState.primaryColor}
                  onChange={handleColorChange('primaryColor')}
                  disableAlpha={true}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography>Secondary</Typography>
              <ColorBox bgcolor={settingsState.secondaryColor} />
              <Box
                sx={{
                  '& .chrome-picker': {
                    width: '100% !important',
                    boxShadow: 'none',
                  },
                }}
              >
                <ChromePicker
                  color={settingsState.secondaryColor}
                  onChange={handleColorChange('secondaryColor')}
                  disableAlpha={true}
                />
              </Box>
            </Grid>
          </Grid>
        </StyledPaper>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Timer Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography>Work Time: {settingsState.workTime} min</Typography>
            <Slider
              value={settingsState.workTime}
              onChange={handleSliderChange('workTime')}
              onChangeCommitted={(event, newValue) => {
                updateSetting("workTime", newValue);
              }}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={60}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>Short Break: {settingsState.shortBreakTime} min</Typography>
            <Slider
              value={settingsState.shortBreakTime}
              onChange={handleSliderChange('shortBreakTime')}
              onChangeCommitted={(event, newValue) => {
                updateSetting("shortBreakTime", newValue);
              }}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={30}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>Long Break: {settingsState.longBreakTime} min</Typography>
            <Slider
              value={settingsState.longBreakTime}
              onChange={handleSliderChange('longBreakTime')}
              onChangeCommitted={(event, newValue) => {
                updateSetting("longBreakTime", newValue);
              }}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={60}
            />
          </Grid>
        </Grid>

        <StyledPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settingsState.notificationsEnabled}
                onChange={updateNotificationEnabled}
              />
            }
            label="Enable Notifications"
          />
          <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
            {`※OSの設定でブラウザからの通知を許可しないと通知が届かないため注意してください。`}
          </Typography>
          {settingsState.notificationsEnabled && (
            <>
              <Box sx={{ mt: 3 }}>
                <Typography>Volume</Typography>
                <Slider
                  value={settingsState.volume}
                  onChange={(event, newValue) => {
                    setSettingsState((prev) => ({ ...prev, volume: newValue }));
                    sound1Ref.current.volume = newValue / 100;
                    sound2Ref.current.volume = newValue / 100;
                  }}
                  onChangeCommitted={(event, newValue) => {
                    updateSetting("volume", newValue);
                  }}
                  min={0}
                  max={100}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={playSound1}
                >
                  通知音1を再生
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={playSound2}
                  sx={{ ml: 3 }}
                >
                  通知音2を再生
                </Button>
              </Box>
            </>
          )}
        </StyledPaper>

        <Button
          variant="contained"
          color="error"
          fullWidth
          sx={{ mt: 4 }}
          onClick={() => setDeleteOpen(true)}
        >
          Delete Account
        </Button>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>アカウントを削除しますか？</DialogTitle>
          <DialogContent>
            <DialogContentText>
              このアクションは取り消せません。本当にアカウントを削除しますか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                deleteAccount();
                setDeleteOpen(false);
              }}
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default Settings;
